import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import './NetForceCalculator.css';

const COLORS = ['#4f6dff', '#00d4c8', '#f5c842', '#ff4d6d', '#a78bfa', '#34d399', '#fb923c'];

const NetForceCalculator = () => {
    const [numForces, setNumForces] = useState(2);
    const [forces, setForces] = useState([
        { mag: '50', ang: '0' },
        { mag: '30', ang: '90' }
    ]);
    const [results, setResults] = useState(null);
    const [openFaq, setOpenFaq] = useState(null);

    const canvasRef = useRef(null);

    // Initialize forces when numForces changes
    useEffect(() => {
        setForces(prev => {
            const next = [...prev];
            if (numForces > next.length) {
                for (let i = next.length; i < numForces; i++) {
                    next.push({ mag: '', ang: '' });
                }
            } else if (numForces < next.length) {
                return next.slice(0, numForces);
            }
            return next;
        });
    }, [numForces]);

    const adjustForces = (delta) => {
        setNumForces(prev => Math.min(7, Math.max(1, prev + delta)));
    };

    const updateForce = (index, field, value) => {
        const next = [...forces];
        next[index][field] = value;
        setForces(next);
    };

    const calculate = () => {
        let fxSum = 0, fySum = 0;
        forces.forEach(f => {
            const m = parseFloat(f.mag) || 0;
            const a = parseFloat(f.ang) || 0;
            fxSum += m * Math.cos(a * Math.PI / 180);
            fySum += m * Math.sin(a * Math.PI / 180);
        });
        const magnitude = Math.sqrt(fxSum * fxSum + fySum * fySum);
        let angle = Math.atan2(fySum, fxSum) * 180 / Math.PI;
        if (angle < 0) angle += 360;

        setResults({
            magnitude: magnitude.toFixed(2),
            angle: angle.toFixed(2),
            fx: fxSum.toFixed(2),
            fy: fySum.toFixed(2)
        });
    };

    const resetAll = () => {
        setNumForces(2);
        setForces([
            { mag: '50', ang: '0' },
            { mag: '30', ang: '90' }
        ]);
        setResults(null);
    };

    const toggleFaq = (idx) => {
        setOpenFaq(prev => (prev === idx ? null : idx));
    };

    // Drawing Logic
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const W = canvas.width, H = canvas.height;
        const cx = W / 2, cy = H / 2;
        const scale = Math.min(W, H) * 0.35;

        // Detect current theme
        const isDark = document.body.getAttribute('data-theme') === 'dark';
        const canvasBg = isDark ? '#0F1320' : '#F1F4FB';
        const axisColor = isDark ? 'rgba(99, 139, 255, 0.25)' : 'rgba(79, 109, 255, 0.2)';
        const gridColor = isDark ? 'rgba(99, 139, 255, 0.04)' : 'rgba(79, 109, 255, 0.05)';
        const labelColor = isDark ? 'rgba(180, 195, 230, 0.5)' : 'rgba(100, 116, 139, 0.6)';
        const resultantColor = isDark ? '#e2e8f0' : '#1e293b';

        const drawArrow = (ctx, x1, y1, x2, y2, color, lineWidth) => {
            const angle = Math.atan2(y2 - y1, x2 - x1);
            const headLen = 10;
            ctx.strokeStyle = color;
            ctx.fillStyle = color;
            ctx.lineWidth = lineWidth;
            ctx.lineCap = 'round';
            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(x2, y2);
            ctx.lineTo(x2 - headLen * Math.cos(angle - 0.45), y2 - headLen * Math.sin(angle - 0.45));
            ctx.lineTo(x2 - headLen * Math.cos(angle + 0.45), y2 - headLen * Math.sin(angle + 0.45));
            ctx.closePath();
            ctx.fill();
        };

        ctx.clearRect(0, 0, W, H);

        // Background (theme-aware)
        ctx.fillStyle = canvasBg;
        ctx.fillRect(0, 0, W, H);

        // Grid
        ctx.strokeStyle = gridColor;
        ctx.lineWidth = 1;
        for (let x = 0; x <= W; x += 40) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
        for (let y = 0; y <= H; y += 40) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }

        // Axes
        ctx.strokeStyle = axisColor;
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 4]);
        ctx.beginPath(); ctx.moveTo(0, cy); ctx.lineTo(W, cy); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(cx, 0); ctx.lineTo(cx, H); ctx.stroke();
        ctx.setLineDash([]);

        // Axis labels
        ctx.fillStyle = labelColor;
        ctx.font = '11px DM Mono, monospace';
        ctx.fillText('+x', W - 20, cy - 8);
        ctx.fillText('+y', cx + 8, 14);

        let maxMag = 0;
        const activeForces = forces.map((f, i) => {
            const m = parseFloat(f.mag) || 0;
            const a = parseFloat(f.ang) || 0;
            if (m > maxMag) maxMag = m;
            return { m, a, color: COLORS[i % COLORS.length] };
        });

        if (maxMag === 0) maxMag = 1;

        // Draw each force arrow
        activeForces.forEach(({ m, a, color }) => {
            if (m === 0) return;
            const rad = a * Math.PI / 180;
            const len = (m / maxMag) * scale;
            const ex = cx + Math.cos(rad) * len;
            const ey = cy - Math.sin(rad) * len;
            drawArrow(ctx, cx, cy, ex, ey, color, 2.5);
        });

        // Resultant (Calculated in real-time for drawing)
        let rx = 0, ry = 0;
        activeForces.forEach(({ m, a }) => {
            rx += m * Math.cos(a * Math.PI / 180);
            ry += m * Math.sin(a * Math.PI / 180);
        });
        const rm = Math.sqrt(rx * rx + ry * ry);

        if (rm > 0.01) {
            const rlen = (rm / maxMag) * scale;
            const ra = Math.atan2(ry, rx);
            const rex = cx + Math.cos(ra) * rlen;
            const rey = cy - Math.sin(ra) * rlen;
            drawArrow(ctx, cx, cy, rex, rey, resultantColor, 3.5);
            ctx.fillStyle = resultantColor;
            ctx.font = 'bold 11px DM Mono, monospace';
            ctx.fillText('Fnet', rex + 6, rey - 6);
        }

        // Origin dot
        ctx.fillStyle = '#4f6dff';
        ctx.beginPath(); ctx.arc(cx, cy, 4, 0, Math.PI * 2); ctx.fill();

    }, [forces, results]);

    return (
        <div className="netforce-calc-page">
            {/* ── HERO ── */}
            <section className="hero">
                <div className="hero-glow"></div>
                <div className="hero-eyebrow">Free Physics Tool</div>
                <h1>
                    Net Force<br />
                    <span>Calculator</span>
                </h1>
                <p className="hero-sub">
                    Calculate the resultant force acting on a body by combining multiple force vectors with varying magnitudes and directions. Includes real-time vector resolution and force decomposition analysis.
                </p>
                <div className="hero-tags">
                    <span className="tag">Newton's Laws</span>
                    <span className="tag">Vector Addition</span>
                    <span className="tag">Free Body Diagrams</span>
                    <span className="tag">Angle Decomposition</span>
                    <span className="tag">Real-time Results</span>
                </div>
            </section>

            {/* ── CALCULATOR ── */}
            <div className="calc-wrapper" id="calculator">
                <div className="calc-card">
                    <div className="calc-header">
                        <h2>OOK <span>Netforce</span> Calculator</h2>
                        <div className="force-count-control">
                            <label>Number of Forces:</label>
                            <button className="counter-btn" onClick={() => adjustForces(-1)}>−</button>
                            <span id="force-count-display">{numForces}</span>
                            <button className="counter-btn" onClick={() => adjustForces(1)}>+</button>
                        </div>
                    </div>

                    <div className="calc-body">
                        <div className="canvas-area">
                            <canvas ref={canvasRef} width="380" height="380"></canvas>
                        </div>
                        <div className="inputs-panel">
                            <h3>Force Vectors</h3>
                            <div className="forces-list">
                                {forces.map((f, i) => (
                                    <div className="force-row" key={i}>
                                        <div className="force-row-label">
                                            <span className="force-dot" style={{ background: COLORS[i % COLORS.length] }}></span>
                                            Force {i + 1}
                                        </div>
                                        <div className="input-group">
                                            <label>Magnitude (N)</label>
                                            <input
                                                type="number"
                                                placeholder="e.g. 50"
                                                value={f.mag}
                                                step="any"
                                                onChange={(e) => updateForce(i, 'mag', e.target.value)}
                                            />
                                        </div>
                                        <div className="input-group">
                                            <label>Angle (°)</label>
                                            <input
                                                type="number"
                                                placeholder="e.g. 45"
                                                value={f.ang}
                                                step="any"
                                                onChange={(e) => updateForce(i, 'ang', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <button className="calc-btn" onClick={calculate}>⟶ Calculate Net Force</button>
                            <button className="reset-btn" onClick={resetAll}>Reset</button>
                        </div>
                    </div>

                    <div className={`result-box ${results ? 'visible' : ''}`}>
                        <h4>Result</h4>
                        <div className="result-grid">
                            <div className="result-item">
                                <span className="result-val">{results ? results.magnitude : '—'}</span>
                                <span className="result-unit">Magnitude (N)</span>
                            </div>
                            <div className="result-item">
                                <span className="result-val">{results ? results.angle + '°' : '—'}</span>
                                <span className="result-unit">Direction (°)</span>
                            </div>
                            <div className="result-item">
                                <span className="result-val">{results ? results.fx : '—'}</span>
                                <span className="result-unit">Fx Component (N)</span>
                            </div>
                            <div className="result-item">
                                <span className="result-val">{results ? results.fy : '—'}</span>
                                <span className="result-unit">Fy Component (N)</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── STATS STRIP ── */}
            <div className="stats-strip">
                <div className="stats-inner">
                    <div>
                        <div className="stat-val">∞</div>
                        <div className="stat-label">Force Combinations</div>
                    </div>
                    <div>
                        <div className="stat-val">360°</div>
                        <div className="stat-label">Full Angle Support</div>
                    </div>
                    <div>
                        <div className="stat-val">0ms</div>
                        <div className="stat-label">Compute Latency</div>
                    </div>
                    <div>
                        <div className="stat-val">100%</div>
                        <div className="stat-label">Free to Use</div>
                    </div>
                </div>
            </div>

            {/* ── WHAT IS NET FORCE ── */}
            <section className="content-section">
                <span className="section-eyebrow">Fundamentals</span>
                <h2 className="section-title">What Is Net Force?</h2>
                <p className="section-lead">
                    Net force is the single equivalent force that has the same effect on an object as all individual forces combined. Understanding it is foundational to classical mechanics and engineering analysis.
                </p>

                <div className="what-is-grid">
                    <div className="info-card">
                        <div className="info-card-icon">⃗F</div>
                        <h3>Vector Quantity</h3>
                        <p>Net force is a vector — it has both magnitude (strength in Newtons) and direction (angle in degrees). Unlike scalar quantities, you cannot simply add force values together without accounting for their directions.</p>
                    </div>
                    <div className="info-card">
                        <div className="info-card-icon">⚖️</div>
                        <h3>Newton's First Law</h3>
                        <p>When the net force on an object is zero, it remains at rest or continues moving at constant velocity. This state is called <em>equilibrium</em> — a critical concept in structural and mechanical engineering.</p>
                    </div>
                    <div className="info-card">
                        <div className="info-card-icon">📐</div>
                        <h3>Component Method</h3>
                        <p>Complex force problems are solved by decomposing each force into horizontal (Fx) and vertical (Fy) components using trigonometry, then summing each axis independently before recombining.</p>
                    </div>
                    <div className="info-card">
                        <div className="info-card-icon">🚀</div>
                        <h3>Newton's Second Law</h3>
                        <p>The net force directly determines an object's acceleration: <strong>F = ma</strong>. A non-zero net force always produces acceleration in the direction of the resultant vector.</p>
                    </div>
                </div>
            </section>

            <div className="divider"></div>

            {/* ── HOW FORCES INTERACT ── */}
            <section className="forces-interact-bg">
                <div className="content-section">
                    <span className="section-eyebrow">Physics Concepts</span>
                    <h2 className="section-title">How Forces Interact</h2>
                    <p className="section-lead">Understanding how multiple forces combine is essential for solving real-world mechanics problems.</p>

                    <div className="steps-timeline">
                        <div className="nf-step-item">
                            <div className="nf-step-num">01</div>
                            <div className="step-content">
                                <h3>Forces in the Same Direction</h3>
                                <p>When two or more forces act along the same line in the same direction, their magnitudes are directly added together. This is the simplest case of force combination and results in a larger net force in that direction.</p>
                            </div>
                        </div>
                        <div className="nf-step-item">
                            <div className="nf-step-num">02</div>
                            <div className="step-content">
                                <h3>Forces in Opposite Directions</h3>
                                <p>Opposing forces partially or fully cancel each other. The net force equals the difference between them and acts in the direction of the larger force. If equal in magnitude, the object is in translational equilibrium.</p>
                            </div>
                        </div>
                        <div className="nf-step-item">
                            <div className="nf-step-num">03</div>
                            <div className="step-content">
                                <h3>Perpendicular Force Components</h3>
                                <p>When forces act at right angles to each other, the Pythagorean theorem gives the resultant magnitude, and the inverse tangent function yields the direction angle. This forms the basis of vector addition in 2D space.</p>
                            </div>
                        </div>
                        <div className="nf-step-item">
                            <div className="nf-step-num">04</div>
                            <div className="step-content">
                                <h3>Forces at Arbitrary Angles</h3>
                                <p>The most general case requires decomposing each force into x and y components using cos(θ) and sin(θ), summing the components independently, then computing the resultant with the Pythagorean theorem and arctan.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <div className="divider"></div>

            {/* ── HOW TO CALCULATE ── */}
            <section className="content-section">
                <span className="section-eyebrow">Mathematical Method</span>
                <h2 className="section-title">How to Calculate Net Force</h2>
                <p className="section-lead">The calculation follows a systematic vector decomposition and recombination process, suitable for any number of forces at any angles.</p>

                <div className="formula-block">
                    <div className="formula-display">
                        <span className="var">F</span><sub>net</sub>
                        <span className="eq"> = </span>
                        <span className="sym">√</span><span>(</span><span className="var">ΣFx</span><sup>2</sup><span> + </span><span className="var">ΣFy</span><sup>2</sup><span>)</span>
                    </div>
                    <p className="formula-note">Where <strong>ΣFx</strong> is the sum of all horizontal components and <strong>ΣFy</strong> is the sum of all vertical components.</p>
                </div>

                <div className="formula-block">
                    <div className="formula-display">
                        <span className="var">θ</span>
                        <span className="eq"> = </span>
                        <span className="sym">tan</span><sup>-1</sup><span>(</span><span className="var">ΣFy</span><span> / </span><span className="var">ΣFx</span><span>)</span>
                    </div>
                    <p className="formula-note">The resultant direction angle θ is measured from the positive x-axis (right horizontal).</p>
                </div>

                <div className="example-box">
                    <h4>Worked Example</h4>
                    <p><strong>Given:</strong> Two forces act on an object:</p>
                    <ul>
                        <li>F₁ = 50 N at 0° (horizontal right)</li>
                        <li>F₂ = 30 N at 90° (vertical upward)</li>
                    </ul>
                    <p style={{ marginTop: '12px' }}><strong>Step 1 — Decompose:</strong></p>
                    <ul>
                        <li>F₁x = 50 cos(0°) = 50 N &nbsp;&nbsp; F₁y = 50 sin(0°) = 0 N</li>
                        <li>F₂x = 30 cos(90°) = 0 N &nbsp;&nbsp; F₂y = 30 sin(90°) = 30 N</li>
                    </ul>
                    <p style={{ marginTop: '12px' }}><strong>Step 2 — Sum components:</strong> ΣFx = 50 N, ΣFy = 30 N</p>
                    <p><strong>Step 3 — Resultant:</strong> F = √(50² + 30²) = √(2500 + 900) = <strong>58.31 N</strong></p>
                    <p><strong>Direction:</strong> θ = tan⁻¹(30/50) = <strong>30.96° above horizontal</strong></p>
                </div>
            </section>

            {/* ── USE CASES ── */}
            <div className="forces-interact-bg">
                <section className="content-section">
                    <span className="section-eyebrow">Applications</span>
                    <h2 className="section-title">Common Use Cases</h2>
                    <p className="section-lead">Net force analysis applies across a wide spectrum of scientific and engineering disciplines.</p>

                    <div className="use-cases-grid">
                        <div className="use-card">
                            <div className="use-card-num">01 / APPLICATION</div>
                            <h3>Inclined Plane Problems</h3>
                            <p>Gravity splits into components parallel and perpendicular to the slope. Combined with friction and normal forces, net force determines acceleration down the ramp.</p>
                        </div>
                        <div className="use-card">
                            <div className="use-card-num">02 / APPLICATION</div>
                            <h3>Elevator Dynamics</h3>
                            <p>Analyze the interplay between weight, tension, and applied forces to calculate whether an elevator accelerates, decelerates, or moves at constant velocity.</p>
                        </div>
                        <div className="use-card">
                            <div className="use-card-num">03 / APPLICATION</div>
                            <h3>Structural Engineering</h3>
                            <p>Bridges, cranes, and buildings rely on equilibrium analysis. Engineers verify that the net force at every joint is zero to ensure structural integrity.</p>
                        </div>
                        <div className="use-card">
                            <div className="use-card-num">04 / APPLICATION</div>
                            <h3>Projectile & Orbital Motion</h3>
                            <p>Gravity as net downward force drives projectile trajectories. In orbital mechanics, net gravitational force equals centripetal force, determining orbital speed.</p>
                        </div>
                        <div className="use-card">
                            <div className="use-card-num">05 / APPLICATION</div>
                            <h3>Object in Free Fall</h3>
                            <p>With only gravity acting, the net force equals the weight (mg). Adding air resistance reduces the net downward force, leading to terminal velocity over time.</p>
                        </div>
                        <div className="use-card">
                            <div className="use-card-num">06 / APPLICATION</div>
                            <h3>Vehicle Braking Analysis</h3>
                            <p>Braking force, aerodynamic drag, and rolling resistance combine to create the net deceleration force. Essential for safety system design and stopping-distance calculations.</p>
                        </div>
                    </div>
                </section>
            </div>

            <div className="divider"></div>

            {/* ── FAQ ── */}
            <section className="content-section">
                <span className="section-eyebrow">FAQ</span>
                <h2 className="section-title">Frequently Asked Questions</h2>
                <p className="section-lead">Quick answers to the most common questions about net force and how to use this calculator.</p>

                <div className="faq-list">
                    {[
                        {
                            q: "What units does this net force calculator use?",
                            a: "This calculator uses SI units: force in Newtons (N), mass in kilograms (kg), acceleration in m/s², and angles in degrees. To convert from other units (e.g., pounds-force or kN), divide by 4.448 (lbf→N) or multiply by 1000 (kN→N) before entering values."
                        },
                        {
                            q: "How do I input a downward (gravity) force?",
                            a: "Use an angle of 270° (or equivalently −90°). For a standard gravity force on mass m, enter magnitude = m × 9.81 at 270°. Angles are always measured counterclockwise from the positive x-axis (rightward)."
                        },
                        {
                            q: "What does equilibrium mean in this context?",
                            a: "An object is in translational equilibrium when the net force equals zero (ΣF = 0). This means it is either stationary or moving with constant velocity — consistent with Newton’s First Law. The calculator flags this condition automatically when |Fnet| < 0.001 N."
                        },
                        {
                            q: "Can I calculate net force in 3D?",
                            a: "This tool currently solves 2D (planar) force systems using x and y components. For 3D problems, you would need to also decompose forces into a z-component and apply the same vector addition principle: Fnet = √(ΣFx² + ΣFy² + ΣFz²)."
                        },
                        {
                            q: "What is the difference between net force and resultant force?",
                            a: "Net force and resultant force are used interchangeably in classical mechanics. Both refer to the single equivalent vector force that produces the same effect on a body as all individual forces acting simultaneously. Some textbooks prefer 'resultant force' in statics and 'net force' in dynamics, but the mathematical calculation is identical."
                        }
                    ].map((faq, idx) => (
                        <div className={`faq-item ${openFaq === idx ? 'open' : ''}`} key={idx}>
                            <button className="faq-q" onClick={() => toggleFaq(idx)}>
                                {faq.q}
                                <span className="faq-icon"><ChevronDown size={18} style={{ transform: openFaq === idx ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }} /></span>
                            </button>
                            <div className="faq-a">{faq.a}</div>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
};

export default NetForceCalculator;
